import { gql } from "@apollo/client";
import FormSection from "controls/FormSection";
import FormText from "controls/FormText";
import MultiValuesField from "controls/MultiValuesField";
import { UNRESTRICTED_SPORTS } from "helpers/constants";
import toCapitalizedWords from "helpers/toCapitalizedWords";
import useData from "hooks/useData";
import useIsAdmin from "hooks/useIsAdmin";
import NumberInput from "inputs/NumberInput";
import SelectInput from "inputs/SelectInput";
import SwitchInput from "inputs/SwitchInput";
import TextInput from "inputs/TextInput";
import React from "react";
import ReadminObjectInput from "readmin_pages/shared/ReadminObjectInput";

export const CompetitionsFormFragment = gql`
  fragment CompetitionsFormFragment on Competition {
    id
    name
    sport
    active
    org {
      id
      allowEncodeHighQualityOutputs
    }
    adPreset {
      id
    }
    potwEnabled
    aiStatsEnabled
    encodeHighQualityOutputs
    videoRecordingPermitted
    sharingDisabled
    anonymisePeople
    pointsForWin
    pointsForDraw
    pointsForLoss
    scoreboardFoulsVisible
    leaderboardTeamStandingVisible
    external
  }
`;

export default function CompetitionsForm({ competitions }) {
  const [data] = useData(gql`
    query CompetitionsForm {
      sports
    }
  `);

  competitions = competitions?.map((competition) => ({
    active: true,
    videoRecordingPermitted: true,
    pointsForWin: 2,
    pointsForDraw: 1,
    pointsForLoss: 0,
    ...competition,
  }));

  const isAdmin = useIsAdmin();
  const existingSports = competitions?.filter((c) => c.id)?.map((c) => c.sport) ?? [];
  const hasAdminOnlySport = !isAdmin && existingSports.some((s) => s && !UNRESTRICTED_SPORTS.includes(s));
  // Include any legacy sport values (e.g. miniball) present on existing records but absent from Org::SPORTS
  const augmentedSports = [...(data?.sports ?? []), ...existingSports.filter((s) => s && !data?.sports?.includes(s))];

  return (
    <>
      <FormText uiStringKey="readmin.competition.description" />
      <MultiValuesField
        label="Organisation"
        name="org"
        required
        disabled={competitions?.some((competition) => competition.id)}
        defaultValues={competitions?.map((competition) => competition.org)}
        input={<ReadminObjectInput optionsTypename="Org" />}
        helperTextUiStringKey="readmin.competition.fields.org"
      />
      <MultiValuesField
        label="Sport"
        name="sport"
        required
        defaultValues={competitions?.map((competition) => competition.sport)}
        disabled={hasAdminOnlySport}
        input={
          <SelectInput
            options={augmentedSports
              .filter((sport) => isAdmin || UNRESTRICTED_SPORTS.includes(sport) || existingSports.includes(sport))
              .map((sport) => [toCapitalizedWords(sport), sport])}
          />
        }
        helperTextUiStringKey="readmin.competition.fields.sport"
      />
      <MultiValuesField
        label="Name"
        name="name"
        required
        disabled={competitions?.some((competition) => competition.external)}
        defaultValues={competitions?.map((competition) => competition.name)}
        input={<TextInput />}
        helperTextUiStringKey="readmin.competition.fields.name"
      />
      <MultiValuesField
        label="Active"
        name="active"
        defaultValues={competitions?.map((competition) => competition.active)}
        input={<SwitchInput />}
        helperTextUiStringKey="readmin.competition.fields.active"
      />
      <MultiValuesField
        label="Ad Group"
        name="adPreset"
        required
        defaultValues={competitions?.map((competition) => competition.adPreset)}
        input={<ReadminObjectInput optionsTypename="AdPreset" />}
        helperTextUiStringKey="readmin.competition.fields.ad_preset"
      />
      {
        // TODO: remove age-based org default then use <SwitchInput> for these two flags
        // these two flags were using <SelectInput> to implement nullable / three-state boolean
        // this is just to implement allowing age-bades org default
      }
      <MultiValuesField
        label="Plays of the Week"
        name="potwEnabled"
        defaultValues={competitions?.map((competition) => competition.potwEnabled)}
        input={
          <SelectInput
            options={[
              ["Enabled", true],
              ["Disabled", false],
            ]}
          />
        }
        helperTextUiStringKey="readmin.competition.fields.potw_enabled"
      />
      {isAdmin && (
        <MultiValuesField
          label="AI Stats"
          name="aiStatsEnabled"
          defaultValues={competitions?.map((competition) => competition.aiStatsEnabled)}
          input={
            <SelectInput
              options={[
                ["Enabled", true],
                ["Disabled", false],
              ]}
            />
          }
          helperTextUiStringKey="readmin.competition.fields.ai_stats_enabled"
        />
      )}
      {(isAdmin || competitions?.some((competition) => competition.org?.allowEncodeHighQualityOutputs)) && (
        <FormSection header="Encoding Settings" defaultExpanded>
          <MultiValuesField
            label="Encode High Quality Outputs"
            name="encodeHighQualityOutputs"
            defaultValues={competitions?.map((competition) => competition.encodeHighQualityOutputs)}
            input={<SwitchInput />}
            helperTextUiStringKey="readmin.competition.fields.encode_high_quality_outputs"
          />
        </FormSection>
      )}
      <FormSection header="Privacy Settings" defaultExpanded>
        <MultiValuesField
          label="Video Recording Permitted"
          name="videoRecordingPermitted"
          defaultValues={competitions?.map((competition) => competition.videoRecordingPermitted)}
          input={
            <SelectInput
              options={[
                ["Permitted", true],
                ["Not Permitted", false],
              ]}
            />
          }
          helperTextUiStringKey="readmin.competition.fields.video_recording_permitted"
        />
        <MultiValuesField
          label="Disable Sharing"
          name="sharingDisabled"
          defaultValues={competitions?.map((competition) => competition.sharingDisabled)}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.competition.fields.sharing_disabled"
        />
        <MultiValuesField
          label="Anonymise People"
          name="anonymisePeople"
          defaultValues={competitions?.map((competition) => competition.anonymisePeople)}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.competition.fields.anonymise_people"
        />
      </FormSection>
      <FormSection header="Leaderboard Settings" defaultExpanded>
        <MultiValuesField
          label="Show Team Standing"
          name="leaderboardTeamStandingVisible"
          defaultValues={competitions?.map((competition) => competition.leaderboardTeamStandingVisible)}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.competition.fields.leaderboard_team_standing_visible"
        />
        <MultiValuesField
          label="Points for Win"
          name="pointsForWin"
          defaultValues={competitions?.map((competition) => competition.pointsForWin)}
          input={<NumberInput />}
          required
          helperTextUiStringKey="readmin.competition.fields.points_for_win"
        />
        <MultiValuesField
          label="Points for Draw"
          name="pointsForDraw"
          defaultValues={competitions?.map((competition) => competition.pointsForDraw)}
          input={<NumberInput />}
          required
          helperTextUiStringKey="readmin.competition.fields.points_for_draw"
        />
        <MultiValuesField
          label="Points for Loss"
          name="pointsForLoss"
          defaultValues={competitions?.map((competition) => competition.pointsForLoss)}
          input={<NumberInput />}
          required
          helperTextUiStringKey="readmin.competition.fields.points_for_loss"
        />
      </FormSection>
      <FormSection header="Live Scoreboard Settings" defaultExpanded>
        <MultiValuesField
          label="Show Fouls"
          name="scoreboardFoulsVisible"
          defaultValues={competitions?.map((competition) => competition.scoreboardFoulsVisible)}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.competition.fields.scoreboard_fouls_visible"
        />
      </FormSection>
    </>
  );
}
